/* Thin client for the Zana briefing endpoints on the PEAR payments backend.
   The dashboard READS aiBriefings/aiInsights straight from Firestore (client-side,
   like every other tab); this is only for the "Regenerate" button, which asks the
   Render backend to force a fresh briefing. Base URL from REACT_APP_PAYMENTS_URL,
   same convention as paymentsApi.ts. */

import { auth } from '../firebase';
import { fetchTakeover, TakeoverRequest, TakeoverScript } from './zanaTakeover';
import {
  canAnswerLocally,
  isConnectionMetaQuestion,
  isTakeoverRequest,
  isReasoningRequest,
  isOperatorTriageAsk,
  localAskZana,
  LocalAskContext,
  serversUnreachableMessage,
} from './zanaLocalAsk';
import {
  getPaymentsBaseUrl,
  isPaymentsApiConfigured,
  paymentsApiUrl,
  zanaServerSetupHint,
} from '../utils/paymentsBaseUrl';

/** Don't hang the dock waiting on a dead Render / local :8080. */
const ASK_TIMEOUT_MS = 35000;
const HEALTH_PROBE_MS = 2500;
const ASK_RETRY_MS = 600;

export async function probePaymentsHealth(): Promise<boolean> {
  if (!isPaymentsApiConfigured()) return false;
  const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = ctrl ? window.setTimeout(() => ctrl.abort(), HEALTH_PROBE_MS) : 0;
  try {
    const res = await fetch(paymentsApiUrl('/health'), { method: 'GET', signal: ctrl?.signal });
    if (!res.ok) return false;
    const json = await res.json().catch(() => ({}));
    return json?.ok === true || json?.service === 'pear-payments';
  } catch {
    return false;
  } finally {
    if (timer) window.clearTimeout(timer);
  }
}

export function isBriefingsConfigured(): boolean {
  return isPaymentsApiConfigured();
}

export interface RegenerateResult {
  ok: boolean;
  result?: { orgCode: string; state?: string; source?: string; daysLeft?: number };
  message?: string;
  /** Configure only, at the top level: what the server decided to DO about the
   *  request — `started`, `already_running`, or `fresh` (nothing to run). The
   *  poll needs this: two of the three mean no write is coming. */
  state?: 'started' | 'already_running' | 'fresh';
  source?: string;
}

export interface AskResult {
  ok: boolean;
  answer?: string;
  mascotState?: string;
  source?: 'ai' | 'deterministic' | 'cache' | 'static';
  message?: string;
  /** Allow-listed action intents — dashboard executes; writes confirm first. */
  intents?: Array<{
    actionId: string;
    params?: { tab?: string; busId?: string; entityType?: string; entityId?: string; scriptId?: string; title?: string };
    confirmRequired?: boolean;
  }>;
  /** The owner handed over the wheel AND named the job, so the engine picked a
   *  walkthrough instead of offering a menu. The dock starts it without a tap:
   *  the ask itself was the consent. Absent when she needs to ask which.
   *  Prefer scriptId (named job); topic drives a composed lens walk. */
  takeover?: {
    scriptId?: string;
    topic?: string;
    ops?: Record<string, string> | null;
    /** Server says skip Just do it / Walk me through — owner already consented. */
    autoRun?: boolean;
  };
  /** The offer that closes every answer: explain in chat, or hand her the screen. */
  takeoverOffer?: {
    topic: string;
    kind: 'do' | 'show';
    /** Primary / takeover button words. */
    label: string;
    /** Stay-in-chat button — answer above is enough. */
    explainLabel?: string;
    /** The question above the buttons. */
    prompt: string;
  };
  /** Zana's reasoning status on THIS answer:
   *   'unavailable' — the deeper reasoning genuinely didn't run (a fault; PEAR
   *                   is alerted server-side). Answer is live facts + honesty.
   *   'limited'     — she's *resting*: a lot of deep thinking recently, so this
   *                   is a quick answer. Nothing is wrong; full depth returns on
   *                   a clock. Never alerts PEAR. Carries `limit` below. */
  aiStatus?: 'unavailable' | 'limited';
  /** Present when aiStatus === 'limited'. Drives the "resting" pill, the reset
   *  countdown and the "get more Zana usage credits" prompt. Never names a model. */
  limit?: ZanaLimit;
}

/** The resting state, from the reasoning meter. `resetAtMs` is when full depth
 *  returns; `reason` is 'window' (a few hours) or 'daily' (tomorrow); `scope`
 *  says whether it was this seat or the whole org that ran the meter down. */
export interface ZanaLimit {
  state: 'resting';
  reason: 'window' | 'daily';
  scope: 'org' | 'user' | null;
  used?: number;
  max?: number;
  resetAtMs: number;
  upgrade?: boolean;
}

/** The dock's usage bar — reasoning window + today's email allowance. */
export interface ZanaUsage {
  tier: 'premium' | 'enterprise' | 'basic';
  ai: {
    state: 'ok' | 'resting';
    scope: 'org' | 'user' | null;
    used: number;
    limit: number;
    fraction: number;
    windowHours: number;
    resetAtMs: number;
    upgrade: boolean;
  };
  email: {
    state: 'ok' | 'full';
    used: number;
    limit: number;
    fraction: number;
    resetAtMs: number;
    upgrade: boolean;
  };
}

/** Store a takeover receipt as typed fleet memory — fire-and-forget. */
export async function recordZanaReceipt(orgCode: string, receipt: string): Promise<void> {
  if (!isBriefingsConfigured()) return;
  const text = receipt.trim();
  if (text.length < 8) return;
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) return;
    await fetch(paymentsApiUrl('/api/premium/zana/receipt'), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        orgCode,
        receipt: text.slice(0, 400),
        receiptId: `rcpt_${Date.now()}`,
      }),
    });
  } catch {
    /* Memory is a nicety — must not block the UI. */
  }
}

/** Read the reasoning + email meters for the dock's progress bar. Returns null
 *  on any failure — the bar is a nicety and must never break chat. */
export async function fetchZanaUsage(orgCode: string): Promise<ZanaUsage | null> {
  if (!isBriefingsConfigured()) return null;
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) return null;
    const res = await fetch(
      paymentsApiUrl(`/api/premium/zana/usage?orgCode=${encodeURIComponent(orgCode)}`),
      { headers: { Authorization: `Bearer ${token}` } },
    );
    if (!res.ok) return null;
    const body = await res.json();
    return body && body.ok ? (body.usage as ZanaUsage) : null;
  } catch {
    return null;
  }
}

/** What acting on Zana's findings has been worth (zanaSavings/{orgCode}, see
 *  pear-payments-backend/src/premium/savingsLedger.js). Null when it can't be read. */
export interface ZanaSavings {
  lifetimeKES: number; thisMonthKES: number; lastMonthKES: number; entryCount: number;
  firstMonth: string | null; monthsTracked: number;
  basis: { title: string; busId: string | null; savingsKES: number; fixedAt: number }[];
}
export async function fetchZanaSavings(orgCode: string): Promise<ZanaSavings | null> {
  if (!isBriefingsConfigured()) return null;
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) return null;
    const res = await fetch(
      paymentsApiUrl(`/api/premium/zana/savings?orgCode=${encodeURIComponent(orgCode)}`),
      { headers: { Authorization: `Bearer ${token}` } },
    );
    if (!res.ok) return null;
    const body = await res.json();
    return body && body.ok && body.savings ? (body.savings as ZanaSavings) : null;
  } catch {
    return null;
  }
}

/** Sync Learner coverage — powers "still learning your fleet". */
export async function fetchZanaSync(orgCode: string): Promise<{
  coveragePct?: number;
  learningLabel?: string;
  pressure?: number;
  archetype?: string;
  topFindingTitle?: string | null;
} | null> {
  if (!isBriefingsConfigured()) return null;
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) return null;
    const res = await fetch(
      paymentsApiUrl(`/api/premium/zana/sync?orgCode=${encodeURIComponent(orgCode)}`),
      { headers: { Authorization: `Bearer ${token}` } },
    );
    if (!res.ok) return null;
    const body = await res.json();
    return body && body.ok ? (body.sync || null) : null;
  } catch {
    return null;
  }
}

/** Guide Mode Decide — proactive soft-open or reaction to owner reply. */
export async function fetchZanaGuide(
  orgCode: string,
  opts?: { text?: string; pageContext?: AskPageContext; mode?: 'proactive' | 'reactive'; liveCounts?: Record<string, unknown> },
): Promise<{
  ok: boolean;
  decision?: {
    action?: string;
    softOpen?: boolean;
    headline?: string;
    finding?: { title?: string; type?: string; key?: string; sev?: string; busId?: string } | null;
    lens?: { topic?: string };
    reason?: string;
    badge?: boolean;
  };
  learningLabel?: string | null;
  coveragePct?: number | null;
  nlu?: { intent?: string; confidence?: number };
} | null> {
  if (!isBriefingsConfigured()) return null;
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) return null;
    const res = await fetch(paymentsApiUrl('/api/premium/zana/guide'), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        orgCode,
        text: opts?.text,
        pageContext: opts?.pageContext,
        mode: opts?.mode || (opts?.text ? 'reactive' : 'proactive'),
        /* 2026-09-29: what the open console can see — the server drops a
           day-old finding these figures contradict (resolved incidents). */
        ...(opts?.liveCounts ? { liveCounts: opts.liveCounts } : {}),
      }),
    });
    if (!res.ok) return null;
    const body = await res.json();
    return body && body.ok ? body : null;
  } catch {
    return null;
  }
}

/** What the owner is currently looking at — lets Zana resolve "this one" /
 *  "why is it flagged?" to the right screen or entity. All optional.
 *  `page` is the visible surface (tab or overlay), not only the shell tab. */
/** What the owner is looking at — the screen, and what that screen is showing.
 *  `cards` and `focus` are the difference between "why is fuel up?" answered
 *  about the fleet in general and answered about the three rows in front of
 *  them. Ids are re-validated against the org's own data server-side. */
export interface AskPageContext {
  page?: string;
  entityType?: 'bus' | 'route' | 'driver';
  entityId?: string;
  /** Headline figures rendered on the current screen, as the owner reads them. */
  cards?: { label: string; value: string }[];
  /** The rows listed on that screen — what "these" and "them" mean. */
  focus?: { type: 'bus' | 'route' | 'driver'; id: string }[];
  /** Period the screen is showing, e.g. "last 30 days". */
  window?: string;
  /** Plate of the bus sheet open on this screen. The server checks it is a real bus. */
  selected?: { bus: string };
  /** Plates actually visible in the page body, in reading order. */
  visible_rows?: string[];
}

/** One prior chat turn, oldest-first, so follow-up questions resolve. */
export interface AskTurn { from: 'you' | 'zana'; text: string }

/** Ask Zana a free-text question about this org; she answers from its own data.
 *  `liveCounts` is the open dashboard's High Risk / unread numbers so she cannot
 *  contradict the cards the owner is staring at.
 *  `local` lets greetings / on-screen facts / cached briefing answer instantly
 *  (and when the payments server is unreachable). */
export async function askZana(
  orgCode: string,
  question: string,
  context?: AskPageContext,
  history?: AskTurn[],
  liveCounts?: { unreadAlertCount?: number; highRiskBusCount?: number; unreadBusIds?: string[]; missingRefuelCount?: number },
  local?: LocalAskContext,
  episodes?: Array<{
    type?: string;
    name?: string;
    summary?: string;
    topic?: string;
    kind?: string;
    goal?: string;
    tabsVisited?: string[];
  }>,
): Promise<AskResult> {
  const cardNumber = (label: string): number | undefined => {
    const card = context?.cards?.find((c) => c.label.toLowerCase() === label.toLowerCase());
    if (!card) return undefined;
    const value = Number(String(card.value).replace(/[^\d.-]/g, ''));
    return Number.isFinite(value) ? value : undefined;
  };
  const localCtx: LocalAskContext = {
    brief: local?.brief,
    unreadAlertCount: liveCounts?.unreadAlertCount ?? local?.unreadAlertCount,
    highRiskBusCount: liveCounts?.highRiskBusCount ?? local?.highRiskBusCount,
    busCount: local?.busCount ?? cardNumber('Fleet buses'),
    activeBusCount: local?.activeBusCount ?? cardNumber('Active buses'),
    page: context?.page || local?.page,
    entityType: context?.entityType || local?.entityType,
    entityId: context?.entityId || local?.entityId,
    cards: context?.cards || local?.cards,
    paymentsConfigured: isPaymentsApiConfigured(),
  };

  /* The judgement vetoes run BEFORE every local branch, connection-meta
     included. They used to sit only inside canAnswerLocally, so the
     connection-meta branch above it was a hole: "take over and check the server
     connection" and "why is the API down?" both matched a connectivity probe
     and were answered locally — dodging the very takeover/reasoning vetoes
     83f6d44 added. A takeover request must always reach the server, because the
     client has no takeover engine to hand it to. */
  const mustReachServer = isTakeoverRequest(question)
    || isReasoningRequest(question)
    || isOperatorTriageAsk(question);

  if (!mustReachServer && isConnectionMetaQuestion(question)) {
    localCtx.paymentsReachable = await probePaymentsHealth();
    const conn = localAskZana(question, localCtx);
    if (conn) return conn;
  }

  // Instant path — no "thinking…" for hello / live counts / cached overview.
  if (!mustReachServer && canAnswerLocally(question)) {
    const instant = localAskZana(question, localCtx);
    if (instant) return instant;
  }

  if (!isPaymentsApiConfigured()) {
    return localAskZana(question, localCtx)
      || { ok: false, message: 'Zana backend not configured' };
  }

  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      return localAskZana(question, localCtx)
        || { ok: false, message: 'Not signed in' };
    }
    /* One request body, one attempt each, and a FRESH deadline per attempt.
     *
     * Both attempts used to share a single AbortController and a single timer,
     * which broke the retry in both directions. A network failure at t=34s
     * produced a "retry" against a controller that was about to abort — or had
     * already aborted — so the retry was a no-op at exactly the moment it was
     * needed. And a network-layer failure does not mean the server did not
     * process the request: the answer cache is only written after the model
     * returns, so both attempts miss it and both call the model. One reply,
     * two charges.
     *
     * The retry is now only for a request that plainly never landed, and it
     * gets its own clock. */
    const body = JSON.stringify({
      orgCode,
      question,
      ...(context ? { context } : {}),
      ...(history && history.length ? { history: history.slice(-6) } : {}),
      ...(episodes && episodes.length ? {
        episodes: episodes.slice(-3).map((e) => ({
          name: String(e.name || '').slice(0, 80),
          summary: String(e.summary || '').slice(0, 400),
          topic: String(e.topic || '').slice(0, 40),
          kind: String(e.kind || '').slice(0, 20),
          goal: String(e.goal || '').slice(0, 160),
          tabsVisited: Array.isArray(e.tabsVisited) ? e.tabsVisited.map(String).slice(0, 8) : [],
        })),
      } : {}),
      ...(liveCounts ? { liveCounts } : {}),
    });
    const attempt = async (): Promise<Response> => {
      const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
      const timer = ctrl ? window.setTimeout(() => ctrl.abort(), ASK_TIMEOUT_MS) : 0;
      try {
        return await fetch(paymentsApiUrl('/api/premium/zana/ask'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body,
          signal: ctrl?.signal,
        });
      } finally {
        if (timer) window.clearTimeout(timer);
      }
    };

    let res: Response;
    try {
      res = await attempt();
    } catch (firstErr) {
      /* A timeout is NOT retried. The server is still working on it — and
         still being billed for it — so asking again buys a second charge for
         the same question. Only a request that failed to reach the server at
         all is worth repeating. */
      const aborted = firstErr instanceof Error && firstErr.name === 'AbortError';
      if (aborted) throw firstErr;
      await new Promise((r) => window.setTimeout(r, ASK_RETRY_MS));
      res = await attempt();
    }
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      return localAskZana(question, localCtx)
        || { ok: false, message: json.message || json.error || 'Zana could not answer that' };
    }
    return json as AskResult;
  } catch (err) {
    const aborted = err instanceof Error && err.name === 'AbortError';
    const local = localAskZana(question, localCtx);
    if (local) return local;
    if (isConnectionMetaQuestion(question)) {
      return localAskZana(question, { ...localCtx, paymentsReachable: false })
        || { ok: false, message: serversUnreachableMessage(), aiStatus: 'unavailable' };
    }
    if (aborted) {
      /* This sentence used to read: "That took longer than 35s — the server may
       * be waking up. Try a shorter question, or tap Test Zana AI for a quick
       * check." Three things wrong with it, all of them ours:
       *
       *   - "Try a shorter question" blames the owner's typing for our latency.
       *     Their question was fine. Ours was the slow part.
       *   - "tap Test Zana AI" hands a diagnostic tool to someone who wanted an
       *     answer about their buses. That is our job, not theirs.
       *   - "may be waking up" hedges about a system we own and can observe.
       *
       * Say what happened, take the blame, and leave them somewhere useful —
       * the screen counts are real and instant even when the model is not. */
      return {
        ok: false,
        message: "That one's on me — I didn't answer in time. Ask me again and it usually lands; "
          + 'the first question after a quiet spell is the slow one. '
          + "Anything already on this screen — bus counts, alerts, fuel totals — I can still tell you straight away.",
        aiStatus: 'unavailable',
      };
    }
    return { ok: false, message: serversUnreachableMessage() };
  }
}

/** Drop Zana's cached dossier after a write so the next Ask matches the UI. */
/**
 * Ask the server to have the org context ready.
 *
 * Called when Zana OFFERS a walkthrough, not when one is accepted. The context
 * behind a compose is cached for ten minutes, so whoever asks first after a
 * quiet spell waits for the rebuild — that wait is the whole of bug 59, and it
 * lands on the one person who has just committed to something.
 *
 * Deliberately not awaited anywhere: it is a hint, and a failed hint must never
 * be the reason a walkthrough does not start.
 */
export async function warmZanaContext(orgCode: string): Promise<void> {
  if (!isPaymentsApiConfigured() || !orgCode) return;
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) return;
    await fetch(paymentsApiUrl('/api/premium/zana/warm-context'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ orgCode }),
    });
  } catch {
    /* best-effort */
  }
}

export async function bridgeZanaThread(
  orgCode: string,
  messages: Array<{ from: 'you' | 'zana'; text: string }>,
): Promise<void> {
  if (!isPaymentsApiConfigured() || !orgCode || !messages.length) return;
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) return;
    await fetch(paymentsApiUrl('/api/premium/zana/bridge-thread'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ orgCode, messages: messages.slice(-24) }),
    });
  } catch {
    /* best-effort — the archive still lands locally */
  }
}

export async function invalidateZanaContext(orgCode: string): Promise<void> {
  if (!isPaymentsApiConfigured() || !orgCode) return;
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) return;
    await fetch(paymentsApiUrl('/api/premium/zana/invalidate-context'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ orgCode }),
    });
  } catch {
    /* best-effort */
  }
}

/** Force a fresh Zana briefing (respects the premium gate + kill-switch server-side). */
export async function regenerateBriefing(orgCode: string): Promise<RegenerateResult> {
  if (!isPaymentsApiConfigured()) return { ok: false, message: 'Zana backend not configured' };
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) return { ok: false, message: 'Not signed in' };
    const res = await fetch(paymentsApiUrl('/api/premium/briefing/regenerate'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ orgCode }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, message: json.message || json.error || 'Could not regenerate' };
    return json as RegenerateResult;
  } catch {
    return { ok: false, message: 'Could not reach Zana — check your connection.' };
  }
}

/** Kick off Zana's staged configure: real reads (inventory → telemetry →
 *  analysis → briefing) checkpointed to aiBriefings/{org}, which the cards
 *  watch via onSnapshot — so the progress bar tracks actual work. */
/* Shared Zana-briefing fetch. The dashboard reads the briefing THROUGH the
   backend (not client-side Firestore) so security rules can't wall the owner
   off from a briefing the backend wrote. Small cache + in-flight dedupe so the
   four Zana surfaces mounting together make ONE request. */
let _briefCache: Record<string, { data: any; at: number }> = {};
let _briefInflight: Record<string, Promise<any> | undefined> = {};

export async function fetchZanaBriefing(orgCode: string, opts?: { force?: boolean }): Promise<any | null> {
  if (!isPaymentsApiConfigured() || !orgCode) return null;
  const cached = _briefCache[orgCode];
  if (!opts?.force && cached && Date.now() - cached.at < 2500) return cached.data;
  if (!opts?.force && _briefInflight[orgCode]) return _briefInflight[orgCode];
  const run = (async () => {
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) return null;
      const res = await fetch(paymentsApiUrl('/api/premium/zana/briefing'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ orgCode }),
      });
      const json = await res.json().catch(() => ({}));
      const data = json && json.ok ? (json.briefing ?? null) : null;
      _briefCache[orgCode] = { data, at: Date.now() };
      return data;
    } catch {
      return _briefCache[orgCode]?.data ?? null;
    } finally {
      _briefInflight[orgCode] = undefined;
    }
  })();
  _briefInflight[orgCode] = run;
  return run;
}

export async function configureZana(orgCode: string): Promise<RegenerateResult> {
  if (!isPaymentsApiConfigured()) return { ok: false, message: 'Zana backend not configured' };
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) return { ok: false, message: 'Not signed in' };
    const res = await fetch(paymentsApiUrl('/api/premium/zana/configure'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ orgCode }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, message: json.message || json.error || 'Could not configure Zana' };
    return json as RegenerateResult;
  } catch {
    return { ok: false, message: 'Could not reach Zana — check your connection.' };
  }
}

function isFinishedBriefing(b: { source?: string } | null | undefined): boolean {
  return !!b && b.source !== 'configuring' && b.source !== 'learning';
}

/* ── How old is the briefing on screen? ───────────────────────────────────
   The backend stamps generatedAtMs and a 12h expiresAtMs on every briefing and
   the dashboard read NEITHER, so a briefing frozen since 2026-07-28 rendered
   exactly like one written this morning — four weeks of July numbers (3 buses,
   0 learned routes, July's pump price) with nothing on screen to doubt.

   The refresh runs daily, so being past the 12h expiry is normal for most of
   the day and must not cry wolf. Missing a whole cycle (26h) means the refresh
   has actually stopped, and that is worth shouting about. */
// Re-exported so existing importers keep one obvious place to reach for it.
export { asList } from '../utils/asList';

export const BRIEFING_STALE_AFTER_MS = 26 * 60 * 60 * 1000;

export function briefingAge(
  brief: { generatedAtMs?: number } | null | undefined,
): { label: string; stale: boolean; atMs: number } | null {
  const at = Number(brief?.generatedAtMs);
  if (!Number.isFinite(at) || at <= 0) return null;
  const ms = Date.now() - at;
  const stale = ms > BRIEFING_STALE_AFTER_MS;
  const mins = Math.round(ms / 60000);
  if (mins < 2) return { label: 'just now', stale, atMs: at };
  if (mins < 60) return { label: `${mins} min ago`, stale, atMs: at };
  const hours = Math.round(mins / 60);
  if (hours < 24) return { label: `${hours}h ago`, stale, atMs: at };
  const days = Math.round(hours / 24);
  if (days <= 7) return { label: `${days} day${days === 1 ? '' : 's'} ago`, stale, atMs: at };
  return {
    label: new Date(at).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }),
    stale,
    atMs: at,
  };
}

/**
 * How long to wait before the next poll, given how long we have been waiting.
 *
 * The backend rate limiter is 30 requests/minute keyed by IP, shared by every
 * route the dashboard calls. A flat 2s poll is exactly 30/min: it leaves no
 * headroom at all, so the configure kick itself, a Zana question, a second tab
 * or a colleague behind the same office NAT tips it into 429s — and a 429 is
 * indistinguishable from "no briefing" one layer down. Stay quick while the
 * stage bar is moving, then back off; a configure past 30s is waiting on the
 * model, not on a stage write.
 */
function pollDelay(elapsedMs: number): number {
  return elapsedMs < 30_000 ? 2000 : 5000;
}

/**
 * Configure or regenerate, while polling the briefing doc in parallel.
 *
 * Configure is intentionally fire-and-poll: the HTTP kick returns in
 * milliseconds; the dashboard watches aiBriefings until source leaves
 * configuring/learning (or we time out with a clear error). Awaiting the old
 * sync configure hung past proxy limits and looked like a silent loop.
 */
export async function runZanaFleetRead(
  orgCode: string,
  opts: {
    mode: 'configure' | 'regenerate';
    onBrief: (brief: any | null) => void;
    /** Default 3 minutes — buildOrgAggregate + briefing can be slow on cold orgs. */
    maxPollMs?: number;
  },
): Promise<RegenerateResult> {
  const maxPollMs = opts.maxPollMs ?? 180_000;

  if (opts.mode === 'configure') {
    const kick = await configureZana(orgCode);
    if (!kick.ok) return kick;

    // The server already had a fresh briefing — nothing is running, so polling
    // for it would just burn requests waiting for a write that will not come.
    if (kick.state === 'fresh') {
      const b = await fetchZanaBriefing(orgCode, { force: true });
      if (b) opts.onBrief(b);
      return kick;
    }

    const started = Date.now();
    let last: any | null = null;
    while (Date.now() - started < maxPollMs) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, pollDelay(Date.now() - started)));
      // eslint-disable-next-line no-await-in-loop
      const got = await fetchZanaBriefing(orgCode, { force: true });
      // A failed read is not evidence the briefing vanished. Handing the UI a
      // null on a 429 or a dropped request drops it out of "Zana is reading
      // your fleet…" back to the empty learning state mid-run — the exact
      // silent-loop symptom the background configure was built to end.
      if (got) { last = got; opts.onBrief(got); }
      if (isFinishedBriefing(last)) {
        return {
          ok: true,
          result: { orgCode, state: 'generated', source: last.source },
        };
      }
      // Honest failure stamped by the backend catch path.
      if (last?.headline && /hit a snag/i.test(String(last.headline))) {
        return {
          ok: false,
          message: last.summary || 'Could not finish reading the fleet. Please try again.',
        };
      }
    }
    if (kick.state === 'already_running') {
      return {
        ok: false,
        message: 'A configure was already running for this fleet and has not finished. '
          + 'Give it a minute, then refresh.',
      };
    }
    return {
      ok: false,
      message: last?.source === 'configuring'
        ? 'Configure is still running on the server — wait a minute and refresh, or try again.'
        : 'Configure did not finish in time. Try again in a minute.',
    };
  }

  /* What the briefing was BEFORE we asked for a new one.
     Regenerate's success test used to be isFinishedBriefing(finalBrief) — true
     of ANY briefing that isn't mid-configure, including one written four weeks
     ago. So when the server genuinely failed, this function read the untouched
     old document, decided it looked finished, and returned ok:true. Gabriel
     pressed Regenerate twice against a briefing frozen since 2026-07-28 and was
     told both times that it worked. Success now means one thing only: the
     document MOVED. */
  const priorBrief = await fetchZanaBriefing(orgCode, { force: true });
  const priorAtMs = Number(priorBrief?.generatedAtMs) || 0;
  const advancedPast = (b: any | null): boolean => (Number(b?.generatedAtMs) || 0) > priorAtMs;

  let stop = false;
  const poll = (async () => {
    const started = Date.now();
    while (!stop && Date.now() - started < maxPollMs) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, pollDelay(Date.now() - started)));
      if (stop) break;
      // eslint-disable-next-line no-await-in-loop
      const b = await fetchZanaBriefing(orgCode, { force: true });
      // Same rule as the configure poll: only report what we actually read.
      if (b) opts.onBrief(b);
      // Stop when the NEW briefing lands — not merely because an old one exists.
      if (advancedPast(b) && isFinishedBriefing(b)) {
        stop = true;
        return;
      }
    }
  })();

  const result = await regenerateBriefing(orgCode);
  const finalBrief = await fetchZanaBriefing(orgCode, { force: true });
  if (finalBrief) opts.onBrief(finalBrief);
  stop = true;
  await poll;

  // The HTTP call failed but the write clearly landed (a timeout on our side,
  // a dropped response) — that IS a success, and only a moved timestamp proves
  // it. A failure with an unmoved document keeps its real error message.
  if (!result.ok && advancedPast(finalBrief) && isFinishedBriefing(finalBrief)) {
    return {
      ok: true,
      result: { orgCode, state: 'generated', source: finalBrief.source },
    };
  }
  return result;
}

export interface ZanaDiagnosis {
  enabledFlag: boolean;
  provider: string;
  model: string;
  hasApiKey: boolean;
  apiKeyPrefix: string | null;
  killSwitchOn: boolean;
  ok: boolean;
  sample: string | null;
  error: string | null;
  httpStatus?: number;
}

/** One real model call + a plain report of what happened — surfaces the actual
 *  provider error instead of the silent deterministic fallback. Admin of a
 *  premium org only (server-gated). */
export async function zanaSelfTest(orgCode: string): Promise<{ ok: boolean; diagnosis?: ZanaDiagnosis; message?: string }> {
  if (!isPaymentsApiConfigured()) return { ok: false, message: 'Zana backend not configured' };
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) return { ok: false, message: 'Not signed in' };
    const res = await fetch(paymentsApiUrl('/api/premium/zana/selftest'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ orgCode }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, message: json.message || json.error || 'Self-test failed' };
    return json as { ok: boolean; diagnosis?: ZanaDiagnosis };
  } catch {
    return { ok: false, message: 'Could not reach Zana — check your connection.' };
  }
}

/** Server /health plus one Zana AI ping — used by the dock "Test connection" chip. */
export async function runZanaConnectionTest(orgCode: string): Promise<string> {
  if (!isPaymentsApiConfigured()) {
    return 'Zana server URL is not configured for this build (REACT_APP_PAYMENTS_URL).';
  }
  const apiUp = await probePaymentsHealth();
  if (!apiUp) {
    return `I can't reach the Trasse server, so I can't test Zana AI yet. ${zanaServerSetupHint()}`;
  }
  if (!orgCode) return 'Server is up, but I need your org to test Zana AI.';
  const res = await zanaSelfTest(orgCode);
  const d = res.diagnosis;
  if (d?.ok && d.sample) {
    const sample = d.sample.length > 120 ? `${d.sample.slice(0, 120)}…` : d.sample;
    return `✓ Connected. Server is healthy and Zana AI answered: “${sample}”`;
  }
  if (d && !d.ok) {
    const why = d.error || res.message || 'Zana AI did not respond';
    if (d.killSwitchOn) return `Server is up, but Zana AI is paused on the server. ${why}`;
    if (!d.hasApiKey) return `Server is up, but Zana AI is not wired yet (no API key on the server). ${why}`;
    return `Server is up, but I'm on offline rules right now — facts and navigation still work. ${why}`;
  }
  return res.message || 'Connection test could not finish — try again in a moment.';
}

/**
 * Email the owner the shortlist Zana kept after triaging the alert queue.
 *
 * Fail-soft on purpose, and never throws: the clearing has already happened in
 * Firestore by the time this runs. Reporting a failed triage because the mail
 * leg died would send the owner back to re-run it over an empty queue.
 */
export async function sendAlertDigest(
  orgCode: string,
  kept: Array<{ busId?: string; busName?: string; type?: string; message?: string }>,
  clearedCount: number,
): Promise<{ emailed: boolean; reason?: string }> {
  if (!isPaymentsApiConfigured()) return { emailed: false, reason: 'not_configured' };
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) return { emailed: false, reason: 'not_signed_in' };
    const res = await fetch(paymentsApiUrl('/api/premium/zana/alert-digest'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ orgCode, kept, clearedCount }),
    });
    if (!res.ok) return { emailed: false, reason: `http_${res.status}` };
    const json = await res.json();
    return { emailed: json?.emailed === true, reason: json?.reason || undefined };
  } catch {
    return { emailed: false, reason: 'network' };
  }
}

export interface BusDriver {
  uid: string;
  name: string;
  email: string;
  phone: string;
  reachable: boolean;
}

/** Who drives this bus. The dashboard asks so a confirm chip can show a real
 *  name — Zana proposes the action with a plate and never sees the person. */
export async function fetchBusDrivers(
  orgCode: string,
  busId: string,
): Promise<{ ok: boolean; plate?: string; drivers: BusDriver[]; reason?: string }> {
  if (!isPaymentsApiConfigured()) return { ok: false, drivers: [], reason: 'not_configured' };
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) return { ok: false, drivers: [], reason: 'not_signed_in' };
    const url = `${paymentsApiUrl('/api/premium/zana/bus-drivers')}?orgCode=${encodeURIComponent(orgCode)}&busId=${encodeURIComponent(busId)}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return { ok: false, drivers: [], reason: `http_${res.status}` };
    const json = await res.json();
    return { ok: json?.ok === true, plate: json?.plate, drivers: json?.drivers || [], reason: json?.reason };
  } catch {
    return { ok: false, drivers: [], reason: 'network' };
  }
}

/** Email the driver of one bus. One bus, one driver, one confirmed tap. */
export async function notifyBusDriver(
  orgCode: string,
  busId: string,
  note: string,
): Promise<{ ok: boolean; plate?: string; driverName?: string; reason?: string; message?: string }> {
  if (!isPaymentsApiConfigured()) return { ok: false, reason: 'not_configured' };
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) return { ok: false, reason: 'not_signed_in' };
    const ac = new AbortController();
    const timer = setTimeout(() => ac.abort(), 20000);
    const res = await fetch(paymentsApiUrl('/api/premium/zana/notify-driver'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ orgCode, busId, note }),
      signal: ac.signal,
    }).finally(() => clearTimeout(timer));
    if (!res.ok) {
      // Keep the server's sentence (2026-09-15): the strict org gate's 403 tells
      // a real-but-unnamed admin to open their admin invite.
      const j = await res.json().catch(() => ({}));
      return { ok: false, reason: `http_${res.status}`, message: j.message || j.error };
    }
    return await res.json();
  } catch (e) {
    const aborted = e instanceof Error && e.name === 'AbortError';
    return { ok: false, reason: aborted ? 'timeout' : 'network', message: aborted ? 'That send did not finish, so I stopped it.' : undefined };
  }
}

/** Fetch a resolved takeover script for this org. The auth + base-URL pattern
 *  lives here with every other Zana call; `zanaTakeover.ts` only performs one. */
export async function fetchTakeoverScript(
  orgCode: string,
  req: TakeoverRequest,
): Promise<{ ok: boolean; script?: TakeoverScript; reason?: string }> {
  if (!isPaymentsApiConfigured()) return { ok: false, reason: 'not_configured' };
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) return { ok: false, reason: 'not_signed_in' };
    return await fetchTakeover(getPaymentsBaseUrl(), token, orgCode, req);
  } catch {
    return { ok: false, reason: 'network' };
  }
}

/* 💸 Ask Google what a road this fleet has never driven looks like right now.
 *
 * Gabriel's rule (2026-08-20): answer from the fleet's own trails first — they
 * are free and they are the better answer — then OFFER to check Google, and
 * only spend if the owner says yes. The endpoint enforces the same thing from
 * its side: it refuses and returns `source: 'history'` when two or more of the
 * fleet's own roads already allow a comparison, and caches for six hours per
 * org+bus+side, so a repeated ask costs nothing.
 *
 * Returns the sentence to show. Never throws: a failed road check must not
 * take down the conversation it was offered in.
 */
export async function checkRoadsWithGoogle(
  orgCode: string,
  busId: string,
  side: 'morning' | 'evening',
): Promise<string> {
  if (!isPaymentsApiConfigured() || !orgCode || !busId) {
    return 'I cannot reach the road service from here right now.';
  }
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) return 'Please sign in again — I could not verify who is asking.';
    const res = await fetch(paymentsApiUrl('/api/premium/roads/google-fill'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ orgCode, busId, side }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || !json.ok) {
      return json.message || 'I could not check those roads just now — try again in a moment.';
    }
    const sentence = String(json.sentence || '').trim();
    if (!sentence) return 'I got nothing usable back about those roads.';
    // Name the source, always. "From Google" and "from your own drives" are
    // different claims and the owner is entitled to know which one they got.
    if (json.source === 'history') return `From your own drives: ${sentence}`;
    if (json.source === 'google') return `${sentence}${json.cached ? '' : ''}`;
    return sentence;
  } catch {
    return 'I could not reach the road service just now — try again in a moment.';
  }
}

/* ── Background / showcase agents (Phase 5–6) ─────────────────────────────── */

export interface ZanaAgentRun {
  id: string;
  name: string;
  topic?: string;
  goal?: string;
  status: 'queued' | 'running' | 'done' | 'failed' | 'deferred';
  kind?: 'background' | 'foreground';
  progress?: number;
  estimateMs?: number;
  report?: { summary?: string; changed?: boolean; lines?: string[] };
  spawnLine?: string;
}

export async function listZanaAgentRuns(
  orgCode: string,
): Promise<{ ok: boolean; runs?: ZanaAgentRun[]; battery?: unknown; message?: string }> {
  if (!isPaymentsApiConfigured() || !orgCode) return { ok: false, message: 'not_configured' };
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) return { ok: false, message: 'not_signed_in' };
    const res = await fetch(paymentsApiUrl('/api/premium/zana/agents/list'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ orgCode, limit: 12 }),
    });
    const json = await res.json().catch(() => ({}));
    return json && typeof json === 'object' ? json : { ok: false, message: 'bad_response' };
  } catch {
    return { ok: false, message: 'network' };
  }
}

export async function startZanaBackgroundAgent(
  orgCode: string,
  opts: { topic?: string; goal?: string; background?: boolean },
): Promise<{
  ok: boolean;
  runId?: string;
  name?: string;
  spawnLine?: string;
  message?: string;
  deferred?: boolean;
}> {
  if (!isPaymentsApiConfigured() || !orgCode) return { ok: false, message: 'not_configured' };
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) return { ok: false, message: 'not_signed_in' };
    const res = await fetch(paymentsApiUrl('/api/premium/zana/agents/start'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        orgCode,
        topic: opts.topic || 'alerts',
        goal: opts.goal || '',
        background: opts.background !== false,
      }),
    });
    const json = await res.json().catch(() => ({}));
    return json && typeof json === 'object' ? json : { ok: false, message: 'bad_response' };
  } catch {
    return { ok: false, message: 'network' };
  }
}

